import { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { submitPipelineJob, uploadAudioToS3, startPipeline } from '@/api/pipeline';
import type { AudioTrackInfo, AudioTrackRole, ParticipantInfo } from '@/types/pipeline';

const DAY_OPTIONS = [
  { value: 'Day1', label: 'Day 1' },
  { value: 'Day2', label: 'Day 2' },
  { value: 'Day3', label: 'Day 3' },
  // バックエンド (pipeline_schemas.py) は Day1-Day4 / PRE / POST を許容するので、
  // UI 上の "DayX（4回目以降）" は API 上 Day4 として送る。
  { value: 'Day4', label: 'Day X（4回目以降）' },
];

const ROLE_OPTIONS: { value: AudioTrackRole; label: string; help: string }[] = [
  { value: 'mixed', label: 'mixed', help: 'リミックス済み単一ファイル' },
  { value: 'tr1', label: 'tr1', help: 'コーチマイク（DJI Mic Mini 等）' },
  { value: 'trLR', label: 'trLR', help: '卓上マイク / 参加者用 (L/R)' },
];

interface FileEntry {
  file: File;
  role: AudioTrackRole;
  duration: number; // 秒
}

/** ブラウザの Audio API で音声ファイルの長さ（秒）を取得 */
function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(isFinite(audio.duration) ? audio.duration : 0);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    audio.src = url;
  });
}

function formatDuration(seconds: number): string {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

/** S2: 新規セッション追加画面 */
export function UploadPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dayType, setDayType] = useState('Day1');
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [participants, setParticipants] = useState<ParticipantInfo[]>([
    { name: '', role: 'facilitator' },
  ]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // セッション日付は将来的に backend へ渡すための保持。現状の pipeline API は
  // 受け取らないが UI 上の入力体験のため state で管理しておく。
  useEffect(() => {
    void sessionDate;
  }, [sessionDate]);

  const isAudioFile = (file: File) =>
    file.type.startsWith('audio/') ||
    file.type.startsWith('video/') ||
    /\.(wav|mp3|m4a|flac|mp4|webm|ogg)$/i.test(file.name);

  const inferDefaultRole = (filename: string): AudioTrackRole => {
    const lower = filename.toLowerCase();
    if (/(^|[^a-z])tr1([^a-z]|$)/.test(lower) || lower.startsWith('tr1')) return 'tr1';
    if (/(^|[^a-z])trlr([^a-z]|$)/.test(lower) || lower.startsWith('trlr')) return 'trLR';
    return 'mixed';
  };

  const addFiles = async (incoming: File[]) => {
    const valid = incoming.filter(isAudioFile);
    if (valid.length === 0) return;
    const withMeta: FileEntry[] = [];
    for (const file of valid) {
      const duration = await getAudioDuration(file);
      withMeta.push({ file, role: inferDefaultRole(file.name), duration });
    }
    setEntries((prev) => [...prev, ...withMeta]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    void addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    void addFiles(Array.from(e.target.files ?? []));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRole = (index: number, role: AudioTrackRole) => {
    setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, role } : e)));
  };

  const addParticipant = () => {
    setParticipants((prev) => [...prev, { name: '', role: 'participant' }]);
  };

  const removeParticipant = (index: number) => {
    setParticipants((prev) => prev.filter((_, i) => i !== index));
  };

  const updateParticipant = (index: number, field: 'name' | 'role', value: string) => {
    setParticipants((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleUpload = async () => {
    if (entries.length === 0) return;

    setUploading(true);
    setErrorMessage('');
    setUploadProgress({});

    try {
      // 1. tracks 構築
      const tracks: AudioTrackInfo[] = entries.map((e) => ({
        filename: e.file.name,
        role: e.role,
        content_type: e.file.type || 'audio/wav',
      }));

      // 2. participants_roster をフィルタ（name 空除外）
      const roster = participants
        .filter((p) => p.name.trim().length > 0)
        .map((p) => ({ name: p.name.trim(), role: p.role }));

      // 3. submit
      setStatusMessage('ジョブ投入中...');
      const submitResp = await submitPipelineJob({
        day: dayType,
        cycle: 1,
        tracks,
        use_rag: false,
        ...(roster.length > 0
          ? {
              participants_roster: roster,
              participants: roster.map((r) => r.name),
            }
          : {}),
      });

      const jobId = submitResp.job_id;

      // 4. presigned PUT を並列で実行
      setStatusMessage('音声ファイルをアップロード中...');
      const uploads = entries.map((entry) => {
        const url = submitResp.upload_urls.find(
          (u) => u.role === entry.role && u.filename === entry.file.name,
        );
        if (!url) {
          throw new Error(
            `presigned URL が見つかりません: role=${entry.role}, filename=${entry.file.name}`,
          );
        }
        const key = `${entry.role}/${entry.file.name}`;
        return uploadAudioToS3(url.upload_url, entry.file, (pct) => {
          setUploadProgress((prev) => ({ ...prev, [key]: pct }));
        });
      });
      await Promise.all(uploads);

      // 5. パイプライン起動
      setStatusMessage('処理を開始します...');
      await startPipeline(jobId);

      // 6. 処理状況画面へ遷移（pipeline ジョブの場合は jobId をクエリで渡す）
      navigate(`/sessions/${jobId}/status?jobId=${jobId}`);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { detail?: string; message?: string } } }).response?.data
          ?.detail ??
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        (err as Error).message ??
        'アップロードに失敗しました';
      setErrorMessage(`エラー: ${msg}`);
    } finally {
      setUploading(false);
      setStatusMessage('');
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Link to={`/clients/${clientId}`} className="text-sm text-blue-600 hover:underline">
          ← クライアント詳細
        </Link>
      </div>

      <h1 className="text-xl font-semibold text-gray-900">新規セッション追加</h1>

      <div className="mt-6 max-w-2xl space-y-6">
        {/* Day 種別 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">セッション種別</label>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {DAY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDayType(opt.value)}
                className={`rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                  dayType === opt.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* 実施日 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">実施日</label>
          <input
            type="date"
            value={sessionDate}
            onChange={(e) => setSessionDate(e.target.value)}
            className="mt-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm"
          />
        </div>

        {/* 参加者 */}
        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">参加者</label>
            <button
              type="button"
              onClick={addParticipant}
              className="text-sm text-blue-600 hover:underline"
            >
              + 参加者を追加
            </button>
          </div>
          <div className="mt-2 space-y-2">
            {participants.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={p.role}
                  onChange={(e) => updateParticipant(i, 'role', e.target.value)}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                >
                  <option value="facilitator">コーチ</option>
                  <option value="participant">参加者</option>
                </select>
                <input
                  type="text"
                  placeholder="名前"
                  value={p.name}
                  onChange={(e) => updateParticipant(i, 'name', e.target.value)}
                  className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-sm"
                />
                {participants.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeParticipant(i)}
                    className="text-xs text-red-500 hover:underline"
                  >
                    削除
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 音声ファイル（複数 + role 選択） */}
        <div>
          <label className="block text-sm font-medium text-gray-700">音声ファイル（複数可）</label>
          <p className="mt-1 text-xs text-gray-500">
            単一リミックス済みファイルなら mixed、ZOOM H5 等で別撮りした場合は tr1 / trLR を選択。
          </p>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`mt-2 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
              dragOver
                ? 'border-blue-400 bg-blue-50'
                : entries.length > 0
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,video/*,.wav,.mp3,.m4a,.flac,.mp4,.webm"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <p className="text-3xl text-gray-400">📁</p>
            <p className="mt-2 text-sm text-gray-600">ここにファイルをドラッグ&ドロップ</p>
            <p className="text-xs text-gray-400">
              または クリックしてファイルを選択（複数可）
            </p>
            <p className="mt-1 text-xs text-gray-400">対応形式: WAV, MP3, M4A, FLAC, MP4</p>
          </div>

          {/* 選択済みファイル一覧（role 選択 + duration 表示） */}
          {entries.length > 0 && (
            <div className="mt-3 space-y-2">
              {entries.map((entry, i) => {
                const key = `${entry.role}/${entry.file.name}`;
                const pct = uploadProgress[key];
                return (
                  <div
                    key={`${entry.file.name}-${i}`}
                    className="rounded-lg bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="text-lg">🎵</span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {entry.file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatSize(entry.file.size)} · {formatDuration(entry.duration)}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <select
                          value={entry.role}
                          onChange={(e) => updateRole(i, e.target.value as AudioTrackRole)}
                          disabled={uploading}
                          className="rounded border border-gray-300 px-2 py-1 text-xs"
                          title={ROLE_OPTIONS.find((r) => r.value === entry.role)?.help}
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          disabled={uploading}
                          className="text-xs text-red-500 hover:underline disabled:opacity-50"
                        >
                          削除
                        </button>
                      </div>
                    </div>
                    {pct !== undefined && (
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-gray-100">
                        <div
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-gray-400">{entries.length} ファイル選択済み</p>
            </div>
          )}
        </div>

        {/* ステータス・エラー表示 */}
        {statusMessage && (
          <div className="rounded-md bg-blue-50 px-4 py-2 text-sm text-blue-700">
            {statusMessage}
          </div>
        )}
        {errorMessage && (
          <div className="rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">{errorMessage}</div>
        )}

        {/* 送信 */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleUpload}
            disabled={entries.length === 0 || uploading}
            className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? 'アップロード中...' : 'セッションを追加'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/clients/${clientId}`)}
            disabled={uploading}
            className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}
